import React, { useContext, useEffect, useMemo, useState } from "react";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIMenu from "@mui/material/Menu";
import MUIChip from "@mui/material/Chip";
import MUIBox from "@mui/material/Box";
import MUITypography from "@mui/material/Typography";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Widget from "../../Widget";
import Setting from "../../../Utility/Setting";
import Format from "../../../Utility/Format";

/**
 * Safely reads the configured queue identifiers from a dashboard widget.
 *
 * @param {object|null|undefined} dashboardWidget Dashboard widget record.
 * @returns {Array<string|number>}
 */
function getConfiguredQueueIds(dashboardWidget) {
  if (!dashboardWidget?.jsonConfiguration) {
    return [];
  }

  try {
    const parsedConfiguration = JSON.parse(dashboardWidget.jsonConfiguration);
    return Array.isArray(parsedConfiguration.queue_ids)
      ? parsedConfiguration.queue_ids
      : [];
  } catch (parseError) {
    console.error(
      "Failed to parse Multi Queue widget configuration:",
      parseError,
    );
    return [];
  }
}

/**
 * Reads a queue count from the Multi Queue response.
 *
 * The API returns each queue as `{ totalInQueue: 10, followUpCounts: {...} }`.
 *
 * @param {object|null|undefined} queueData Queue data from the response.
 * @returns {number}
 */
function getQueueCount(queueData) {
  const numericQueueCount = Number(queueData?.totalInQueue);

  return Number.isFinite(numericQueueCount) ? numericQueueCount : 0;
}

/**
 * Adds the counts for every configured queue.
 *
 * @param {object|null|undefined} widgetData Loaded widget response.
 * @param {Array<string|number>} configuredQueueIds Configured queue identifiers.
 * @returns {number}
 */
function getTotalQueueCount(widgetData, configuredQueueIds) {
  return configuredQueueIds.reduce((totalQueueCount, queueId) => {
    return (
      totalQueueCount + getQueueCount(widgetData?.multiQueueResults?.[queueId])
    );
  }, 0);
}

/**
 * Renders the queue selector shown on the right side of the widget.
 */
function WidgetDashboardMultiQueueVisual({
  widgetId,
  widgetData,
  configuredQueueIds,
  queuesIndexed,
  selectedQueueId,
  onSelectedQueueIdChange,
  onUpdateActiveWidgetConfiguration,
  onActiveWidgetChange,
}) {
  const [queueMenuAnchorElement, setQueueMenuAnchorElement] = useState(null);
  const isQueueMenuOpen = Boolean(queueMenuAnchorElement);

  const queueOptions = configuredQueueIds.map((queueId) => ({
    key: queueId,
    value: queueId,
    label: queuesIndexed?.[queueId]?.name ?? `Queue ${queueId}`,
    count: getQueueCount(widgetData?.multiQueueResults?.[queueId]),
  }));

  const handleQueueSelection = (newQueueId) => {
    onSelectedQueueIdChange(newQueueId);
    Setting.set(`dashboard.${widgetId}.selectedQueueId`, newQueueId);
    onUpdateActiveWidgetConfiguration({
      dashboardConfiguration: {
        queueId: newQueueId,
      },
    });
    onActiveWidgetChange?.();
    setQueueMenuAnchorElement(null);
  };

  return (
    <MUIBox sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
      <MUIChip
        color="primary"
        label="Queue"
        clickable={true}
        onClick={(clickEvent) =>
          setQueueMenuAnchorElement(clickEvent.currentTarget)
        }
        onDelete={(clickEvent) =>
          setQueueMenuAnchorElement(clickEvent.currentTarget)
        }
        deleteIcon={<MUIExpandMoreIcon />}
      />
      <MUIMenu
        id={`multi-queue-selection-menu-${widgetId}`}
        anchorEl={queueMenuAnchorElement}
        open={isQueueMenuOpen}
        onClose={() => setQueueMenuAnchorElement(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {queueOptions.length === 0 && (
          <MUIMenuItem disabled>
            <MUITypography variant="body2" color="text.secondary">
              No queues configured
            </MUITypography>
          </MUIMenuItem>
        )}
        {queueOptions.map((queueOption) => (
          <MUIMenuItem
            key={queueOption.key}
            selected={String(queueOption.value) === String(selectedQueueId)}
            onClick={() => handleQueueSelection(queueOption.value)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span>{queueOption.label}</span>
            <MUIBox sx={{ marginLeft: "auto", minWidth: "40px" }}>
              <MUIChip
                label={Format.number(queueOption.count)}
                size="small"
                color="primary"
                sx={{ width: "100%", height: "18px", fontSize: "0.75rem" }}
              />
            </MUIBox>
          </MUIMenuItem>
        ))}
      </MUIMenu>
    </MUIBox>
  );
}

/**
 * Dashboard widget that combines the counts from multiple queues.
 */
export default function WidgetDashboardMultiQueue(props) {
  const mainContext = useContext(MainContext);
  const [selectedQueueId, setSelectedQueueId] = useState(
    () => Setting.get(`dashboard.${props.widgetId}.selectedQueueId`) ?? null,
  );
  const configuredQueueIds = useMemo(
    () => getConfiguredQueueIds(props.dashboardWidget),
    [props.dashboardWidget],
  );

  useEffect(() => {
    if (!props.isActive || selectedQueueId === null) {
      return;
    }

    props.updateActiveWidgetConfiguration({
      dashboardConfiguration: {
        queueId: selectedQueueId,
      },
    });
  }, [props.isActive, props.updateActiveWidgetConfiguration, selectedQueueId]);

  return (
    <Widget
      getWidgetId={() => props.widgetId}
      getTitle={() => props.dashboardWidget.name}
      isActive={() => props.isActive}
      getSubtitle={() => {
        const totalQueues = configuredQueueIds.length;
        return `${Format.number(totalQueues)} ${totalQueues === 1 ? "queue" : "queues"}`;
      }}
      renderContent={({ widgetData }) => {
        const totalQueueCount = getTotalQueueCount(
          widgetData,
          configuredQueueIds,
        );

        return (
          <MUITypography
            component="div"
            variant="h5"
            color={totalQueueCount === 0 ? "success.main" : "text.primary"}
          >
            {Format.number(totalQueueCount)}
          </MUITypography>
        );
      }}
      renderVisual={({ widgetData }) => (
        <WidgetDashboardMultiQueueVisual
          widgetId={props.widgetId}
          widgetData={widgetData}
          configuredQueueIds={configuredQueueIds}
          queuesIndexed={mainContext.queuesIndexed}
          selectedQueueId={selectedQueueId}
          onSelectedQueueIdChange={setSelectedQueueId}
          onUpdateActiveWidgetConfiguration={
            props.updateActiveWidgetConfiguration
          }
          onActiveWidgetChange={props.onActiveWidgetChange}
        />
      )}
    />
  );
}
